/** @jsxImportSource theme-ui */

import React, { PropsWithChildren, useCallback, useState } from 'react';
import { SxProp, ThemeUIStyleObject } from 'theme-ui';

import { isKeyboardSelect } from 'utils/accessibilityHelpers';
import { sxtheme, valignHackTop } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';

type BdAccordionSectionProps = SxProp & {
  title: React.ReactNode;
  onOpen?: () => void;
};

export const BdAccordion: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  return <div sx={{ borderTop: t => `1px solid ${t.colors.deepSea20}` }}>{children}</div>;
};

export const BdAccordionSection: React.FC<PropsWithChildren<BdAccordionSectionProps>> = ({
  title,
  onOpen,
  children,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const titleStyle: ThemeUIStyleObject = {
    cursor: 'pointer',
    backgroundColor: 'white',
    padding: '24px 12px 23px 12px',
    borderBottom: sxtheme(t => `1px solid ${t.colors.deepSea20}`),
    outline: 'none',
    '.icon.bd-icon': {
      transitionDuration: '0.1s',
    },
    '&.open': {
      padding: '24px 12px',
      borderBottom: 'none',
      '.icon.bd-icon': {
        transform: 'rotate(180deg)',
      },
      '.fade-out-expanded': {
        visibility: 'hidden',
        opacity: 0,
        transition: 'visibility 0s linear 100ms, opacity 100ms',
      },
    },
    '&.closed': {
      '.fade-out-expanded': {
        visibility: 'visible',
        opacity: 1,
        transition: 'visibility 0s linear 100ms, opacity 100ms',
      },
    },
    '&:hover': {
      backgroundColor: 'deepSea20',
    },
    '&:focus': {
      backgroundColor: 'white',
      padding: '21px 9px',
      border: sxtheme(t => `3px solid ${t.colors.sea30}`),
      transition: '0.01s all',
    },
    '&:active': {
      backgroundColor: 'seafoam20',
      transition: '0.01s all',
    },
  };
  const contentStyle = {
    background: 'white',
    padding: ['8px 12px 24px 12px', '8px 12px 32px 12px'],
    width: '100%',
    borderBottom: sxtheme(t => `1px solid ${t.colors.deepSea20}`),
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isKeyboardSelect(e)) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    },
    [isOpen]
  );

  return (
    <div>
      <div
        tabIndex={0}
        role="button"
        onKeyDown={handleKeyDown}
        onClick={() => {
          if (!isOpen && onOpen) onOpen();
          setIsOpen(!isOpen);
        }}
        sx={titleStyle}
        className={isOpen ? 'open' : 'closed'}
      >
        <FlexGroup
          alignItems="center"
          suffix={
            <BdIcon
              data-html2canvas-ignore="true"
              name="icon-chevron-down"
              color="sea80"
              sx={valignHackTop('2px')}
            />
          }
        >
          {title}
        </FlexGroup>
      </div>
      {isOpen && <div sx={contentStyle}>{children}</div>}
    </div>
  );
};
